import { useMemo } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import remarkMath from 'remark-math'
import rehypeKatex from 'rehype-katex'
import 'katex/dist/katex.min.css'
import './Markdown.css'

// Guard against malformed model output where math delimiters are unbalanced.
// remark-math pairs `$$`/`$` left-to-right, so a single stray delimiter makes one
// math span swallow the rest of the message (prose, headings, bold) — KaTeX then
// dumps that whole span as a red error blob. When the delimiters don't balance we
// escape every `$`, so the message still renders as structured Markdown (with the
// equations shown as plain text) instead of collapsing. Balanced messages (the
// normal case) are returned unchanged so real math still renders.
function safeMarkdown(text) {
  if (!text) return ''
  // Count unescaped delimiters: `$$` (display) and standalone `$` (inline).
  const withoutEscaped = text.replace(/\\\$/g, '')
  const displayCount = (withoutEscaped.match(/\$\$/g) || []).length
  const inlineCount = (withoutEscaped.replace(/\$\$/g, '').match(/\$/g) || []).length
  const balanced = displayCount % 2 === 0 && inlineCount % 2 === 0
  if (balanced) return text
  // Unbalanced -> neutralize every `$` (escaped `$` renders as a literal dollar
  // sign and is ignored by remark-math), keeping the rest of the Markdown intact.
  return text.replace(/\$/g, '\\$')
}

// Renders an assistant message as Markdown with GitHub-flavored extras (tables,
// lists) and LaTeX math via KaTeX ($...$ inline, $$...$$ block). Links open in a
// new tab.
function Markdown({ children }) {
  const text = useMemo(() => safeMarkdown(children || ''), [children])
  return (
    <div className="msg-markdown">
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[[rehypeKatex, { throwOnError: false }]]}
        components={{
          a: ({ node, ...props }) => (
            <a target="_blank" rel="noopener noreferrer" {...props} />
          ),
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  )
}

export default Markdown
